import Redactor from '../../redactor.esm.min.js'
import { registerToolbarButton, setToolbarButtonHtml } from '../registerButton'
import { applyTextStyle } from '../textStyle'

class MpFontFamilyPlugin {
    static defaultOptions = {
        input: true
    }

    static defaultLang = {
        en: {
            mpFontFamily: {
                title: 'Font Family',
                search: 'Search fonts',
                noResults: 'No fonts found'
            }
        }
    }

    constructor(app, options = {}) {
        this.app = app
        this.options = options
        this.fonts = []
        this.currentlySelectedFont = ''
        this.defaultTitle = 'Font Family'
        this.buttonDropDownIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                <path d="M4.5 6L8.5 10L12.5 6" stroke="#9B9FA6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`
        this.defaultFont = this._stripFontName(options.defaultFont)
        this._onSelectionChange = () => {
            setTimeout(() => this._updateButtonContent(), 10)
        }
    }

    start() {
        registerToolbarButton(this.app, 'mpFontFamily', this._createToolbarButton())
        this.app.on('click', this._onSelectionChange)
        this.app.on('key:up', this._onSelectionChange)
        this.app.on('editor:focus', this._onSelectionChange)
    }

    stop() {
        this.app.off('click', this._onSelectionChange)
        this.app.off('key:up', this._onSelectionChange)
        this.app.off('editor:focus', this._onSelectionChange)
    }

    _createToolbarButton() {
        return {
            title: 'Font Family',
            color: false,
            html: `<div>${this.defaultTitle}</div>
            ${this.buttonDropDownIcon}
            `,
            classname: 'mp-redactor-toolbar-plugin mp-font-family-button',
            position: { before: 'moreinline' },
            command: 'mpFontFamily.popup'
        }
    }

    _updateButtonContent() {
        const selection = this.app.getService('selection')
        const inlines = selection.getNodes({ types: 'inline' }) || []
        const node = inlines.length ? inlines[inlines.length - 1] : null
        const selectedFontFamily = this._authoredFontFamily(node) || this.defaultFont || this.defaultTitle

        this.currentlySelectedFont = selectedFontFamily
        setToolbarButtonHtml(this.app, 'mpFontFamily', `<div>${selectedFontFamily}</div>${this.buttonDropDownIcon}`)
    }

    _stripFontName(fontName) {
        if (!fontName || typeof fontName !== 'string') return ''
        let name = fontName.trim()
        if (name.indexOf(',') !== -1) {
            name = name.split(',')[0].trim()
        }
        name = name.replace(/^["']+|["']+$/g, '')
        return name.trim()
    }

    // The family written in the HTML style attribute. Computed style is the face
    // Chrome paints, and bold Tahoma can come back as Arial Black.
    _authoredFontFamily(node) {
        let el = node && node.nodeType === 1 ? node : node && node.parentElement
        const doc = el && el.ownerDocument
        while (el && el.nodeType === 1 && el !== doc.body && el !== doc.documentElement) {
            const specified = el.style ? this._stripFontName(el.style.getPropertyValue('font-family')) : ''
            if (specified && specified.toLowerCase() !== 'inherit') return specified
            el = el.parentElement
        }
        return ''
    }

    _buildFontItem(font) {
        const labelSuffix = font.sublabel
            ? ` <span style="font-size:11px;color:#9B9FA6;font-family:inherit">${font.sublabel}</span>`
            : ''
        return {
            title: "<div style='font-family: " + font.value + "'>" + font.label + labelSuffix + '</div>',
            command: 'mpFontFamily.set',
            name: [font.label, font.value, font.sublabel].filter(Boolean).join(' '),
            active: this._stripFontName(font.value) == this.currentlySelectedFont,
            params: {
                tag: 'span',
                style: {
                    'font-family': font.value
                }
            }
        }
    }

    popup(_params, { button, el } = {}) {
        this.app.saveSelection()

        const getFonts = this.options.getFonts
        const fonts = (typeof getFonts === 'function' ? getFonts() : this.options.fonts) || []
        const preferredOrder = this.options.groupOrder || ['Brand Fonts', 'Full Support', 'Limited Support']
        const grouped = {}

        fonts.forEach((font) => {
            if (!font || font.value == null) return
            const groupName = font.group || 'Other'
            if (!grouped[groupName]) grouped[groupName] = {}
            const key = String(font.value).toLowerCase()
            if (grouped[groupName][key]) return
            grouped[groupName][key] = this._buildFontItem(font)
        })

        const groupOrder = Object.keys(grouped).sort((a, b) => {
            const ai = preferredOrder.indexOf(a)
            const bi = preferredOrder.indexOf(b)
            if (ai === -1 && bi === -1) return a.localeCompare(b)
            if (ai === -1) return 1
            if (bi === -1) return -1
            return ai - bi
        })

        const useGroups = groupOrder.length > 1 || (groupOrder.length === 1 && groupOrder[0] !== 'Other')
        let items = []
        if (useGroups) {
            items = groupOrder.map((groupName) => ({
                title: groupName,
                items: Object.values(grouped[groupName])
            }))
        } else {
            items = Object.values(grouped[groupOrder[0]] || {})
        }

        const searchPlaceholder = this.app.getLang('mpFontFamily.search') || 'Search fonts'
        this.app.dropdown.create('mpFontFamily', {
            items,
            title: `<div class="mp-font-family-search">
                <input type="search" class="rx-form-input mp-font-family-search-input" placeholder="${searchPlaceholder}" autocomplete="off" />
            </div>`
        })
        this.app.dropdown.open(button || el)
        if (this.app.dropdown.isOpen()) {
            this._bindFontSearch()
        }
    }

    _bindFontSearch() {
        const dropdown = this.app.dropdown.getEl()
        if (!dropdown) return

        const input = dropdown.querySelector('.mp-font-family-search-input')
        if (!input) return

        const emptyEl = document.createElement('div')
        emptyEl.className = 'mp-font-family-search-empty'
        emptyEl.textContent = this.app.getLang('mpFontFamily.noResults') || 'No fonts found'
        emptyEl.hidden = true
        this.app.dropdown.dropdownItems.appendChild(emptyEl)

        const filter = () => this._filterFontItems(dropdown, input.value, emptyEl)
        input.addEventListener('input', filter)
        input.addEventListener('search', filter)
        input.addEventListener('click', (e) => e.stopPropagation())
        input.addEventListener('mousedown', (e) => e.stopPropagation())
        input.addEventListener('keydown', (e) => {
            e.stopPropagation()
            if (e.key === 'Enter') {
                e.preventDefault()
                const first = dropdown.querySelector('.rx-option-item:not(.mp-font-family-hidden)')
                if (first) first.click()
            }
        })

        setTimeout(() => input.focus(), 0)
    }

    _filterFontItems(dropdown, query, emptyEl) {
        const q = (query || '').trim().toLowerCase()
        const items = dropdown.querySelectorAll('.rx-option-item')
        let visibleCount = 0

        items.forEach((item) => {
            const text = ((item.dataset.name || '') + ' ' + (item.textContent || '')).toLowerCase()
            const match = !q || text.indexOf(q) !== -1
            item.classList.toggle('mp-font-family-hidden', !match)
            if (match) visibleCount++
        })

        dropdown.querySelectorAll('.rx-option-header').forEach((header) => {
            const container = header.nextElementSibling
            const groupMatches = q && (header.textContent || '').toLowerCase().indexOf(q) !== -1
            if (groupMatches && container) {
                header.classList.remove('mp-font-family-hidden')
                container.querySelectorAll('.rx-option-item').forEach((item) => {
                    if (item.classList.contains('mp-font-family-hidden')) {
                        item.classList.remove('mp-font-family-hidden')
                        visibleCount++
                    }
                })
                return
            }
            const hasVisible =
                container &&
                Array.from(container.querySelectorAll('.rx-option-item')).some(
                    (item) => !item.classList.contains('mp-font-family-hidden')
                )
            header.classList.toggle('mp-font-family-hidden', !hasVisible)
        })

        if (emptyEl) emptyEl.hidden = visibleCount > 0
    }

    set(params = {}) {
        this.app.dropdown.close()
        const styles = params.styles || params.style
        applyTextStyle(this.app, styles)
        const selectedFontFamily = this._stripFontName(styles && styles['font-family'])
        if (selectedFontFamily) {
            this.currentlySelectedFont = selectedFontFamily
            setToolbarButtonHtml(this.app, 'mpFontFamily', `<div>${selectedFontFamily}</div>${this.buttonDropDownIcon}`)
        }
    }
}

Redactor.addPlugin('mpFontFamily', MpFontFamilyPlugin)
